/**
 * Timer Store for time tracking
 */

import { create } from 'zustand';
import { api } from '@/services/api';

interface ActiveTimer {
  id: string;
  projectId: string;
  taskId: string | null;
  description: string | null;
  startedAt: string;
  project: { id: string; name: string };
  task?: { id: string; title: string } | null;
  runningMinutes?: number;
}

interface TimerState {
  activeTimer: ActiveTimer | null;
  isLoading: boolean;
  elapsedSeconds: number;

  fetchActiveTimer: () => Promise<void>;
  startTimer: (
    projectId: string,
    taskId?: string | null,
    description?: string | null,
  ) => Promise<void>;
  stopTimer: () => Promise<{ duration: number } | null>;
  discardTimer: () => Promise<void>;
  updateDescription: (description: string) => Promise<void>;
  tick: () => void;
}

export const useTimerStore = create<TimerState>((set, _get) => ({
  activeTimer: null,
  isLoading: false,
  elapsedSeconds: 0,

  fetchActiveTimer: async () => {
    set({ isLoading: true });
    try {
      const response = await api.get<ActiveTimer | null>('/timer/active');
      const timer = response.data ?? null;

      if (timer) {
        const startedAt = new Date(timer.startedAt);
        const elapsed = Math.floor((Date.now() - startedAt.getTime()) / 1000);
        set({ activeTimer: timer, elapsedSeconds: elapsed, isLoading: false });
      } else {
        set({ activeTimer: null, elapsedSeconds: 0, isLoading: false });
      }
    } catch {
      set({ activeTimer: null, isLoading: false });
    }
  },

  startTimer: async (projectId, taskId, description) => {
    set({ isLoading: true });
    try {
      const response = await api.post<ActiveTimer>('/timer/start', {
        projectId,
        taskId: taskId ?? null,
        description: description ?? null,
      });

      if (response.data) {
        set({ activeTimer: response.data, elapsedSeconds: 0, isLoading: false });
      }
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  stopTimer: async () => {
    set({ isLoading: true });
    try {
      const response = await api.post<{ duration: number }>('/timer/stop');
      set({ activeTimer: null, elapsedSeconds: 0, isLoading: false });
      return response.data ?? null;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  discardTimer: async () => {
    set({ isLoading: true });
    try {
      await api.post('/timer/discard');
      set({ activeTimer: null, elapsedSeconds: 0, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  updateDescription: async (description: string) => {
    try {
      const response = await api.put<ActiveTimer>('/timer/update', {
        description,
      });
      if (response.data) {
        set((state) => ({
          activeTimer: state.activeTimer ? { ...state.activeTimer, description } : null,
        }));
      }
    } catch (error) {
      throw error;
    }
  },

  tick: () => {
    set((state) => {
      if (!state.activeTimer) return { elapsedSeconds: 0 };
      const startedAt = new Date(state.activeTimer.startedAt).getTime();
      return { elapsedSeconds: Math.floor((Date.now() - startedAt) / 1000) };
    });
  },
}));
